import React, { useEffect, useState } from "react";

import PersonDB from "./PersonDB";

import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import {
  getPersonData,
  getPersonTotalEntries,
  getPersonTotalPages,
  getPerson,
} from "../../redux/reducers/person";

import { parseTermSearchQuery, useQuery } from "../../utils/GeneralUtils";
import { useMetaData } from "../../utils/useMetaData";
import { useLocation, useNavigate } from "react-router-dom";
import { TermType, useGetTerm } from "../../utils/useTerm";
import {
  checkParamExist,
  getCurrentPage,
  getCurrentPerView,
  getParam,
  getParamAll,
  handlePerView,
  searchClearHandler,
  searchHandler,
  SearchParam,
} from "../../common/archive/ListSearchFunc";

const PersonDBContainer: React.FC = () => {
  const dispatch = useAppDispatch();
  const personData = useAppSelector(getPersonData);
  const entries = useAppSelector(getPersonTotalEntries);
  const totalPage = useAppSelector(getPersonTotalPages);

  const [isLoading, setIsLoading] = useState<boolean>(true);

  const query = useQuery();
  const location = useLocation();
  const navigate = useNavigate();

  const {
    termData: { entries: games },
    getTermLoading: isGameLoading,
  } = useGetTerm(TermType.Games, undefined, -1);
  const {
    termData: { entries: playerTypes },
    getTermLoading: isTypeLoading,
  } = useGetTerm(TermType.PlayerTypes, undefined, -1);

  // ターム関連のパラメーターが存在する場合、コンテンツの取得を一時停止します
  const [enabled, setEnabled] = useState<boolean>(
    !checkParamExist(query, [TermType.Games, TermType.PlayerTypes])
  );

  useEffect(() => {
    if (enabled === true) return;

    if (!isGameLoading && !isTypeLoading) {
      setEnabled(true);
    }
  }, [isGameLoading, isTypeLoading]);

  let selectedGames: { ids: Array<string>; names: Array<string> } = {
    ids: [],
    names: [],
  };
  let selectedPlayerTypes: { ids: Array<string>; names: Array<string> } = {
    ids: [],
    names: [],
  };

  if (!isGameLoading && !isTypeLoading) {
    const gameParams = getParamAll(query, "games");
    selectedGames = {
      ids: parseTermSearchQuery(gameParams, games, "id"),
      names: gameParams,
    };

    const typeParams = getParamAll(query, "player_types");
    selectedPlayerTypes = {
      ids: parseTermSearchQuery(typeParams, playerTypes, "id"),
      names: typeParams,
    };
  }

  const searchArgs = {
    search: getParam(query, "s"),
    games: selectedGames.ids.length > 0 ? selectedGames.ids : undefined,
    player_types:
      selectedPlayerTypes.ids.length > 0 ? selectedPlayerTypes.ids : undefined,
    page: getCurrentPage(query),
    per: getCurrentPerView(query),
    verified: getParam(query, "verified"),
    accountType: getParam(query, "sort_jikei") === "true" ? "jikei" : undefined,
  };

  useEffect(() => {
    if (!enabled) return;

    setIsLoading(true);

    const promise = dispatch(getPerson(searchArgs));

    promise.finally(() => setIsLoading(false));

    return () => {
      promise.abort();
    };
  }, [location.search, enabled]);

  useMetaData({
    title: "eスポーツプロフィール一覧",
    description:
      "eスポーツを使ったPRやイベントキャスティングサービス「SELeCT」のプロフィール一覧ページです。eスポーツプロ選手、ストリーマー、ゲーマーなど様々なタイプのプロフィールが掲載されています。オファーやキーワード・カテゴリでの検索をご利用いただけます。",
    imgUrl: "",
  });

  return (
    <PersonDB
      isLoading={isLoading}
      personData={personData}
      totalEntries={entries}
      totalPages={totalPage}
      currentPage={searchArgs.page}
      perView={searchArgs.per}
      keywordParam={searchArgs.search ?? ""}
      gameParams={selectedGames.names}
      typeParams={selectedPlayerTypes.names}
      verifiedParam={searchArgs.verified ?? ""}
      searchHandler={(params: Array<SearchParam>) => {
        searchHandler(query, params, navigate);
      }}
      clearSearch={() => searchClearHandler(navigate)}
      handlePerView={(value: number) =>
        handlePerView(query, location, navigate, value)
      }
      gameOptions={games}
      playerTypeOptions={playerTypes}
      handleSortJikei={(isJikei: string) => {
        searchHandler(
          query,
          [{ field: "sort_jikei", value: isJikei }],
          navigate
        );
      }}
      sortJikei={searchArgs.accountType ? "true" : ""}
    />
  );
};

export default PersonDBContainer;
